import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Box from "../../components/Box";
import Button from "../../components/Button";
import FormulaEditor from "../../components/FormulaEditor";
import Table from "../../components/Table";
import SortableTable from "../../components/SortableTable";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";
import { renderError } from "../../helpers/errors";
import api from "../../services/api";
import { hasPlanFeature } from "../../services/auth";
import PanelTemplate from "../../templates/Panel";
import ModalProductVariable from "./ModalProductVariable";
import SelectCustomization from "../../components/selects/SelectCustomization";

const globalVariables = {
    header: ['Nome', 'Tipo'],
    rows: [
        {
            data: [
                'Imposto', 'Porcentagem'
            ],
            to: '/company/list'
        }
    ]
};

const Calculation = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({header: [], rows: []});
    const [params, setParams] = useState([]);
    const [paramToEdit, setParamToEdit] = useState(null);
    const [createProductVariable, setCreateProductVariable] = useState(false);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);

    const [formulaPrice, setFormulaPrice] = useState('');
    const [formulaPriceId, setFormulaPriceId] = useState('');

    const [formulaCommission, setFormulaCommission] = useState('');
    const [formulaCommissionId, setFormulaCommissionId] = useState('');

    const [defaultCustomizationParamId, setDefaultCustomizationParamId] = useState(null);
    const [defaultCustomizationId, setDefaultCustomizationId] = useState(null);

    const systemVariables = ['Quantidade', 'Custo unitário', 'Gravação', 'Imposto', 'SKU', 'Fornecedor', 'Categoria'];

    const priceVariables = useMemo(() => [
        ...systemVariables,
        ...params.map(p => p.name),
    ], [params]);

    const commissionVariables = useMemo(() => [
        ...priceVariables,
        'Preço de venda',
    ], [priceVariables]);

    useEffect(() => {
        getParams();
        getUsers();
        getRoles();
    }, []);

    useEffect(() => {
        generateData();
    }, [params]);

    const getUsers = () => {
        api.get(`/list`).then(res => {
            setUsers(res.data.resources);
        })
    }

    const getRoles = () => {
        api.get(`/role`).then(res => {
            setRoles(res.data.resources);
        })
    }

    const getParams = () => {
        api.get('/param').then(res => {
            let toParams = [];
            res.data.resources.map(resource => {
                if(resource.type === 'price_calculation'){
                    setFormulaPrice(resource.value ?? '');
                    setFormulaPriceId(resource.id);
                    return;
                }
                if(resource.type === 'commission_calculation'){
                    setFormulaCommission(resource.value ?? '');
                    setFormulaCommissionId(resource.id);
                    return;
                }
                if(resource.type === 'default_customization_id'){
                    setDefaultCustomizationId(resource.value);
                    setDefaultCustomizationParamId(resource.id);
                    return;
                }
                toParams.push(resource);
            })
            setParams(toParams);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const generateData = () => {
        const header = ['Nome', 'Origem'];
        const rows = [];

        const systemRows = ['Quantidade', 'Custo unitário', 'Gravação', 'SKU', 'Fornecedor', 'Categoria'];
        systemRows.forEach(systemRow => {
            rows.push({
                key: systemRow,
                data: [systemRow, 'Sistema']
            });
        });

        params.forEach(param => {
            rows.push({
                id: param.id,
                data: [param.name, 'Personalizada'],
                action: () => setParamToEdit(param)
            });
        });

        setData({header, rows});
    }

    // Reordena as variáveis do produto por arrasta e solta. Atualiza otimista e
    // reverte se o backend falhar.
    const reorderParams = orderedIds => {
        const previous = params;
        setParams(orderedIds.map(id => params.find(param => param.id === id)));

        api.put(`/param/reorder`, { ids: orderedIds }).catch(error => {
            setParams(previous);
            renderError(error);
        });
    }

    const storeFormulaPrice = async () => {
        setLoading(true);

        try{
            await api.put(`/param/${formulaPriceId}`, { 
                name: 'Cálculo do preço de venda',
                type: 'price_calculation',
                value: formulaPrice
            });
            
            toast.success('Fórmula do preço de venda salva com sucesso');
            await api[defaultCustomizationParamId ? 'put' : 'post'](`/param/${defaultCustomizationParamId ?? ''}`, {
                name: 'Gravação padrão',
                type: 'default_customization_id',
                value: defaultCustomizationId
            });

            toast.success('Gravação padrão salva com sucesso');
            
            getParams();
        } catch(error){
            renderError(error);
        } finally{
            setLoading(false);
        }
    }




    const storeFormulaCommission = () => {
        setLoading(true);

        api.put(`/param/${formulaCommissionId}`, {
            name: 'Cálculo da comissão',
            type: 'commission_calculation',
            value: formulaCommission
        }).then(res => {
            toast.success('Fórmula da comissão salva com sucesso');
            getParams();
        }).catch(error => {
            renderError(error);
        }).finally(() => {            
            setLoading(false);
        });
    }
        

    if (!hasPlanFeature('calculation')) {
        return (
            <PlanFeatureUpgradeModal
                feature="calculation"
                show={true}
                close={() => navigate(-1)}
            />
        );
    }

    return (
        <>
            {(paramToEdit !== null || createProductVariable) &&    
                <ModalProductVariable
                    close={() => {
                        setCreateProductVariable(false);
                        setParamToEdit(null);
                    }}
                    loading={loading}
                    setLoading={setLoading}
                    params={params}
                    getParams={getParams}
                    paramToEdit={paramToEdit}
                    users={users}
                    roles={roles}
                />
            }
            <PanelTemplate id="calculation">
                <div className="row">
                    <div className="col-6">
                        <Box
                            title={`Cálculo do preço de venda`}
                        >
                            <h2>Instruções</h2>
                            <p>Use os seguintes caraceteres: +, -, /, *, (, ) <br />
                            Para utilizar variáveis, coloque-as entre colchetes. Exemplo: [Custo unitário]
                            </p>
                            <FormulaEditor
                                label={`Fórmula`} 
                                value={formulaPrice} 
                                change={setFormulaPrice} 
                                variables={priceVariables}
                            />
                            <SelectCustomization
                                label={`Gravação padrão`}
                                selectedCustomization={defaultCustomizationId}
                                setSelectedCustomization={setDefaultCustomizationId}
                                updating={loading}
                            />
                            <p>Esta gravação será utilizada no cálculo automático de preço caso o produto solicitado não tenha nenhuma gravação habilitada.</p>
                        </Box>
                        <Button type={`primary`} size={`small`} svg={`save-white`} loading={loading} text={`Salvar cálculo`} action={storeFormulaPrice} />
                        <Box
                            title={`Cálculo da comissão`}
                        >
                            <h2>Instruções</h2>
                            <p>Use apenas os seguintes caraceteres: +, -, /, *, (, ) <br />
                            Para utilizar variáveis, coloque-as entre colchetes. Exemplo: [Custo unitário] <br />
                            Você pode usar a variável [Preço de venda]</p>
                            <FormulaEditor 
                                label={`Fórmula`}
                                value={formulaCommission} 
                                change={setFormulaCommission} 
                                variables={commissionVariables}
                            />
                        </Box>
                        <Button type={`primary`} size={`small`} svg={`save-white`} loading={loading} text={`Salvar cálculo`} action={storeFormulaCommission} />
                    </div>
                    <div className="col-6">
                        <Box
                            title={`Variáveis globais`}
                            className={`no-padding`}
                            // tools={<Button type={`secondary`} text={`Cadastrar variável`} size={`small`} svg={`plus-blue`} />}
                        >
                            <Table header={globalVariables.header} rows={globalVariables.rows} />
                        </Box>
                        <Box
                            title={`Variáveis do produto`}
                            className={`no-padding`}
                            tools={
                                <Button
                                    type={`secondary`}
                                    text={`Cadastrar variável`}
                                    size={`small`}
                                    svg={`plus-blue`}
                                    action={() => setCreateProductVariable(true)}
                                />
                            }
                        >
                            <SortableTable header={data.header} rows={data.rows} loading={loading} onReorder={reorderParams} minWidth={0} />
                        </Box>
                    </div>
                </div>
            </PanelTemplate>
        </>
    );
}

export default Calculation;